import React, { useState } from 'react';
import { ui } from '../../ui';
import { Category } from '../../api';
import {
  MetricsPreset, METRICS_PRESETS, etToday, presetRange, fmtRangeDate,
} from '../../dateRange';
import { IntradayVolumeChart } from './IntradayVolumeChart';
import { RepTimeline } from './RepTimeline';
import { RepProductivityTable } from './RepProductivityTable';
import { TalkTimeComparison } from './TalkTimeComparison';

interface InsightsPanelProps {
  categories: Category[];
  // Lets the Rep Timeline's "unmapped agents" notice jump to the Agents tab to fix the mapping.
  onGoToAgentsTab?: () => void;
}

type InsightsSubTab = 'intraday' | 'timeline' | 'productivity' | 'talkcompare';
const SUB_TABS: { id: InsightsSubTab; label: string }[] = [
  { id: 'intraday', label: 'Intraday Volume' },
  { id: 'timeline', label: 'Rep Timeline' },
  { id: 'productivity', label: 'Productivity' },
  { id: 'talkcompare', label: 'Talk Time Comparison' },
];

// Communication → Admin → Insights (Admin-only — payroll-adjacent schedule/punch data).
// Container for the four Call Insights reports, each with its own internal fetching/loading/
// error states; this component owns only the sub-nav and the shared date-range control (all
// reports but Rep Timeline, which is single-day and manages its own date picker).
export const InsightsPanel: React.FC<InsightsPanelProps> = ({ categories, onGoToAgentsTab }) => {
  const [subTab, setSubTab] = useState<InsightsSubTab>('intraday');

  // Shared range control — reuses the Metrics tab's exact preset set (Current Day default).
  const [preset, setPreset] = useState<MetricsPreset>('current-day');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const range = preset === 'custom'
    ? (customFrom && customTo && customFrom <= customTo ? { from: customFrom, to: customTo } : null)
    : presetRange(preset);

  // Rep Timeline's single date, independent of the range control above (FR-2.6).
  const [timelineDate, setTimelineDate] = useState(etToday());

  return (
    <section className={ui.sectionCard}>
      <h2 className={`${ui.sectionTitle} mb-4`}>Insights</h2>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        {SUB_TABS.map(t => (
          <button
            key={t.id}
            type="button"
            onClick={() => setSubTab(t.id)}
            aria-pressed={subTab === t.id}
            className={`rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${
              subTab === t.id
                ? 'border-blue-600 bg-blue-600 text-white'
                : 'border-border-default bg-surface-alt text-secondary hover:bg-surface-hover'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {subTab !== 'timeline' && (
        <div className="mb-6 flex flex-wrap items-end gap-3">
          <div>
            <label className={ui.label} htmlFor="insights-range-preset">Date range</label>
            <select
              id="insights-range-preset"
              className={ui.control}
              value={preset}
              onChange={e => setPreset(e.target.value as MetricsPreset)}
            >
              {METRICS_PRESETS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
            </select>
          </div>
          {preset === 'custom' && (
            <>
              <div>
                <label className={ui.label} htmlFor="insights-range-from">Start date</label>
                <input
                  id="insights-range-from"
                  type="date"
                  className={ui.control}
                  value={customFrom}
                  max={customTo || undefined}
                  onChange={e => setCustomFrom(e.target.value)}
                />
              </div>
              <div>
                <label className={ui.label} htmlFor="insights-range-to">End date</label>
                <input
                  id="insights-range-to"
                  type="date"
                  className={ui.control}
                  value={customTo}
                  min={customFrom || undefined}
                  onChange={e => setCustomTo(e.target.value)}
                />
              </div>
            </>
          )}
          <span className="pb-2 text-sm text-muted">
            {range ? `${fmtRangeDate(range.from)} – ${fmtRangeDate(range.to)}` : 'Pick a start and end date.'}
          </span>
        </div>
      )}

      {subTab === 'intraday' && (
        range
          ? <IntradayVolumeChart from={range.from} to={range.to} categories={categories} />
          : <p className="text-sm text-muted">Pick a start and end date.</p>
      )}
      {subTab === 'timeline' && (
        <RepTimeline date={timelineDate} onDateChange={setTimelineDate} onGoToAgentsTab={onGoToAgentsTab} />
      )}
      {subTab === 'productivity' && (
        range
          ? <RepProductivityTable from={range.from} to={range.to} />
          : <p className="text-sm text-muted">Pick a start and end date.</p>
      )}
      {subTab === 'talkcompare' && (
        range
          ? <TalkTimeComparison from={range.from} to={range.to} categories={categories} />
          : <p className="text-sm text-muted">Pick a start and end date.</p>
      )}
    </section>
  );
};
